import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { QUERY_PARAMS } from '../routes/consts';
import { mixpanelService } from '../services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from '../services/mixpanel/consts';
import { isAirgappedMode } from '../utils/env';
import { getQueryParam } from '../utils/url';

/**
 * Hook that tracks page view events when route changes occur
 * Prevents duplicate events for the same page load
 */
export const usePageViewTracking = () => {
  const location = useLocation();
  const prevPathnameRef = useRef<string | undefined>();

  useEffect(() => {
    // Prevent duplicate tracking for the same pathname
    if (prevPathnameRef.current === location.pathname) {
      return;
    }

    if (isAirgappedMode()) {
      return;
    }

    const trafficSource =
      getQueryParam(QUERY_PARAMS.TRAFFIC_SOURCE) ?? undefined;

    mixpanelService.track(MIXPANEL_EVENT_NAMES.VIEW, {
      operation: MIXPANEL_OPERATIONS.PAGE_VIEW,
      interaction_details: {
        page_url: location.pathname,
        page_search: location.search,
        traffic_source: trafficSource,
      },
    });

    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);
};

/**
 * Component wrapper for usePageViewTracking hook
 * Used for backward compatibility with existing code
 */
export const PageViewTracker = () => {
  usePageViewTracking();

  return null;
};
